import * as R from 'Router/routes';
import { Application } from 'hooks/webapi/useApplication';
import { CardType } from 'hooks/webapi/useActiveCard';
import { Client } from 'hooks/webapi/useClient';
import { isValidApplication } from 'utils/applicationUtility';
import { useActiveCard, useApplication, useClient } from 'hooks/webapi';
import { useCallback, useMemo, useRef } from 'react';
import { useInsurancePolicy } from 'pages/InsurancePolicy/useInsurancePolicy';
import { useLocation } from 'react-router-dom';
import useBundle from 'hooks/useBundle';
import usePaymentPreferences, {
  PaymentPreferences,
} from './webapi/usePaymentPreferences';

export type LoanType = 'MAIN' | 'ADDITIONAL';

export function useRepeatedLoanNavigation({
  typeOfLoan = 'MAIN',
}:
  | {
      typeOfLoan?: LoanType;
    }
  | undefined = {}) {
  const { pathname } = useLocation();
  const { client } = useClient();
  const { application } = useApplication();
  const { isBundleAvailable } = useBundle();
  const { getIsInsuranceAvailable, getIsInsurancePolicyEnabled } =
    useInsurancePolicy();
  const { activeCard, fetchActiveCard } = useActiveCard();
  const { paymentPreferences, fetchPaymentPreferences } =
    usePaymentPreferences();

  const getInsuranceOrConfirmScreen = (
    applicationFetched: Application | null | undefined = application,
    clientFetched: Client | null | undefined = client,
  ) => {
    const insuranceAvailable = getIsInsuranceAvailable({
      clientFetched,
      applicationFetched,
    });
    return insuranceAvailable ? R.INSURANCE_POLICY : R.LOAN_CONFIRM;
  };

  const stepInsuranceOrConfirm = getInsuranceOrConfirmScreen(application);

  const bundleAvailableRef = useRef<Boolean>(isBundleAvailable);
  bundleAvailableRef.current = isBundleAvailable;

  const isMain = typeOfLoan === 'MAIN';

  const getCheckDetailsScreen = () => {
    const insuranceDetails = isMain
      ? R.LOAN_CHECK_DETAILS_INSURANCE
      : R.ADDITIONAL_LOAN_CHECK_DETAILS_INSURANCE;
    const isInsurancePolicyEnabled = getIsInsurancePolicyEnabled();
    const loanCheckDetails = isMain
      ? R.LOAN_CHECK_DETAILS
      : R.ADDITIONAL_AMOUNT_CHECK_DETAILS;
    return isInsurancePolicyEnabled ? insuranceDetails : loanCheckDetails;
  };

  // Routes
  const routes = useMemo(() => {
    return {
      autoRepay: isMain ? R.AUTOREPAY_MAIN : R.AUTOREPAY_ADDITIONAL,
      saveCard: isMain ? R.SAVE_CARD_MAIN : R.SAVE_CARD_ADDITIONAL,
      final: getCheckDetailsScreen(),
    };
  }, [isMain]);

  const getAutorepaySavecardRoute = useCallback(
    ({
      preferencesFromParent,
      activeCardFromParent,
    }:
      | {
          preferencesFromParent?: PaymentPreferences | null;
          activeCardFromParent?: CardType | null;
        }
      | undefined = {}) => {
      const paymentPreferencesData =
        preferencesFromParent || paymentPreferences;
      const activeCardData = activeCardFromParent || activeCard;
      if (paymentPreferencesData?.autoRepay) return undefined;
      return !activeCardData ? routes.saveCard : routes.autoRepay;
    },
    [activeCard, paymentPreferences, routes],
  );

  const backRoute = useMemo(() => {
    const fallback = R.REPEATED_LOAN;

    switch (pathname) {
      case R.LOAN_CONFIRM:
        return isBundleAvailable
          ? getAutorepaySavecardRoute() || R.REPEATED_LOAN
          : R.REPEATED_LOAN;

      case R.AUTOREPAY_MAIN:
      case R.AUTOREPAY_ADDITIONAL:
      case R.SAVE_CARD_MAIN:
      case R.SAVE_CARD_ADDITIONAL:
        return isBundleAvailable ? R.REPEATED_LOAN : R.LOAN_CONFIRM;

      default:
        return fallback; // Corregido el return que faltaba
    }
  }, [pathname, isBundleAvailable, getAutorepaySavecardRoute]);

  const nextRoute = useMemo(() => {
    const stepPayment = getAutorepaySavecardRoute();

    switch (pathname) {
      case R.LOGIN:
      case R.ONLINE_LOGIN:
      case R.TWO_FACTOR_AUTH:
        if (application?.status === 'CLOSED') return R.REPEATED_LOAN;
        return (isBundleAvailable && stepPayment) || stepInsuranceOrConfirm;
      case R.REPEATED_LOAN:
        return (isBundleAvailable && stepPayment) || stepInsuranceOrConfirm;
      case R.LOAN_CONFIRM:
        return routes.final;

      case R.AUTOREPAY_MAIN:
      case R.AUTOREPAY_ADDITIONAL:
      case R.SAVE_CARD_MAIN:
      case R.SAVE_CARD_ADDITIONAL:
        return isBundleAvailable ? stepInsuranceOrConfirm : routes.final;

      default:
        return stepInsuranceOrConfirm;
    }
  }, [
    application,
    pathname,
    isBundleAvailable,
    getAutorepaySavecardRoute,
    routes.final,
    stepInsuranceOrConfirm,
  ]);

  const getRepeatedLoginRedirection = async (
    clientFetched: Client | null | undefined,
    applicationFetched: Application | null | undefined,
  ) => {
    const preferencesFromParent = await fetchPaymentPreferences();
    const activeCardFromParent = await fetchActiveCard();
    const stepPayment = getAutorepaySavecardRoute({
      preferencesFromParent,
      activeCardFromParent,
    });
    if (!isValidApplication(applicationFetched, clientFetched))
      return R.REPEATED_LOAN;
    const stepInsuranceOrConfirmFresh = getInsuranceOrConfirmScreen(
      applicationFetched,
      clientFetched,
    );

    const bundleRoute = bundleAvailableRef.current && stepPayment;
    return bundleRoute || stepInsuranceOrConfirmFresh;
  };

  return {
    backRoute,
    nextRoute,
    stepInsuranceOrConfirm,
    getAutorepaySavecardRoute,
    getRepeatedLoginRedirection,
    getInsuranceOrConfirmScreen,
    getCheckDetailsScreen,
  };
}
